/*
  Single.js - Sent SMS Single Component
*/

// NPM MODULES
import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { DateTime } from 'luxon';

// UTIL
import { errorCodes } from '../../../utils/errorCodes';

// FORMATTED MESSAGES
import SentFormattedMessages from '../../../messages/websms/SentFormattedMessages';

// NETWORK
import useSentRequest from '../../../requests/SentRequest';

// COMPONENTS
import SectionWrapper from '../../common/SectionWrapper';
import ConfirmationDialog from './ConfirmationDialog';

const SentSmsSingle = ({
  sms,
  handleBackButton,
  failed = false,
  errorHandler,
}) => {
  const [justCancelled, setJustCancelled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { cancelPendingMessage } = useSentRequest();

  const handleCancelPendingMessage = async () => {
    setShowConfirmation(false);
    try {
      const result = await cancelPendingMessage(sms?.sms_id, errorHandler);
      if (result && result.length > 0 && setJustCancelled)
        setJustCancelled(true);
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const generateBody = () => {
    if (sms) {
      const deliverDate = sms.sms_delivered
        ? DateTime.fromISO(sms.sms_delivered)
        : 'Pending Delivery';
      const sentDate = DateTime.fromISO(sms.sms_sent);
      const rezoned = sentDate.setZone(localStorage.getItem('timeShort'), {
        keepLocalTime: false,
      });

      let smsText;
      try {
        smsText = decodeURIComponent(sms.sms_text);
      } catch (err) {
        smsText = 'ERROR: MALFORMED TEXT';
      }

      return (
        <Box>
          <Typography gutterBottom>
            <strong>{SentFormattedMessages.queuedHeader}</strong>
            {`: ${rezoned.toFormat(
              'dd-LLL-yyyy h:mm a'
            )} ${localStorage.getItem('timeString')}`}
          </Typography>

          <Typography gutterBottom>
            <strong>
              {failed
                ? SentFormattedMessages.failedHeader
                : SentFormattedMessages.sentHeader}
            </strong>
            {`: ${
              sms.sms_delivered
                ? deliverDate
                    .setZone(localStorage.getItem('timeShort'), {
                      keepLocalTime: false,
                    })
                    .toFormat('dd-LLL-yyyy h:mm a')
                : 'Pending Delivery'
            }`}
          </Typography>

          <Typography gutterBottom>
            <strong>{SentFormattedMessages.toHeader}</strong>
            {`: ${sms.dest_name || sms.dest_tn || sms.dest_email}`}
          </Typography>

          <Typography gutterBottom>
            <strong>{SentFormattedMessages.statusHeader}</strong>
            {`: ${
              justCancelled
                ? 'Cancelled'
                : sms.sent_flag_name
                ? sms.sent_flag_name
                : sms.sent_flag
            }`}
          </Typography>

          {generateErrorComponent(sms.error_code_text)}

          <Typography gutterBottom>
            <strong>{SentFormattedMessages.encodingHeader}</strong>
            {`: ${sms.smsc_params}`}
          </Typography>

          <Typography gutterBottom>
            <strong>{SentFormattedMessages.messageHeader}</strong>
            {`: ${smsText}`}
          </Typography>

          {(sms.sent_flag_name === 'Pending' || sms.sent_flag === 'Pending') &&
            !justCancelled && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowConfirmation(true)}
                sx={{ marginY: 2 }}
              >
                {SentFormattedMessages.cancelPendingMessage}
              </Button>
            )}
        </Box>
      );
    }

    return <Typography>No Data</Typography>;
  };

  const generateErrorComponent = (errorCode) => {
    if (!errorCode || typeof errorCode === 'undefined') return;
    const code = errorCodes(errorCode);
    return (
      <>
        <Typography gutterBottom>
          <strong>{SentFormattedMessages.errorCodeHeader}</strong>
          {`: ${code.roxCode}`}
        </Typography>

        <Typography gutterBottom>
          <strong>{SentFormattedMessages.errorTextHeader}</strong>
          {`: ${code.meaning}`}
        </Typography>
      </>
    );
  };

  return (
    <SectionWrapper title="SMS Information" icon="messages">
      <Box>
        <Button
          startIcon={<ChevronLeft />}
          onClick={handleBackButton}
          color="secondary"
          sx={{ marginBottom: 2 }}
        >
          <Typography>
            {failed
              ? SentFormattedMessages.backFailedText
              : SentFormattedMessages.backText}
          </Typography>
        </Button>
      </Box>

      <Typography variant="h3" gutterBottom>
        {SentFormattedMessages.replyDetails}
      </Typography>

      {generateBody()}

      <ConfirmationDialog
        showConfirmation={showConfirmation}
        handleCloseConfirmation={() => setShowConfirmation(false)}
        setJustCancelled={setJustCancelled}
        message={'Are you sure you would like to cancel this pending message?'}
        handleCancelPendingMessage={handleCancelPendingMessage}
      />
    </SectionWrapper>
  );
};

export default SentSmsSingle;
